import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';

type OngoingTrialRow = {
  id: number;
  nct_id?: string | null;
  trial_name?: string | null;
  full_title?: string | null;
  phases?: string | null;
  overall_status?: string | null;
  trial_status?: string | null;
  enrollment_size?: string | null;
  study_start_date?: string | null;
  primary_completion_date?: string | null;
  study_completion_date?: string | null;
  last_updated_post_date?: string | null;
  last_update_submit_date?: string | null;
  indication_details?: string | null;
  sponsor?: string | null;
  linked_asset?: number | null;
  asset_name?: string | null;
  indication_codes?: number[] | null;
  trial_visible_approved?: boolean | null;
};

type IndicationOption = { id: string; name: string };

const ManageOngoingTrials: React.FC = () => {
  const [trials, setTrials] = useState<OngoingTrialRow[]>([]);
  const [indicationOptions, setIndicationOptions] = useState<IndicationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editNames, setEditNames] = useState<Record<number, string>>({});
  const [editTitles, setEditTitles] = useState<Record<number, string>>({});
  const [editIndications, setEditIndications] = useState<Record<number, string>>({});
  const [editSponsors, setEditSponsors] = useState<Record<number, string>>({});
  const [assetOptions, setAssetOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [assetSelections, setAssetSelections] = useState<Record<number, string>>({});
  const [indicationSelections, setIndicationSelections] = useState<Record<number, string[]>>({});
  const [publishedFlags, setPublishedFlags] = useState<Record<number, boolean>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const loadTrials = async (search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const q = (search || '').trim();
      const [response, assetsResponse, indications] = await Promise.all([
        apiService.fetchSuperuserOngoingTrials({
          search: q || undefined,
          limit: q ? 800 : 400,
        }),
        apiService.fetchSuperuserEmergingAssetsPicker(),
        apiService.fetchEmergingIndications(),
      ]);
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load ongoing trials');
      }
      const nextTrials: OngoingTrialRow[] = response.data || [];
      setTrials(nextTrials);
      setAssetSelections(
        nextTrials.reduce<Record<number, string>>((acc, trial) => {
          acc[trial.id] = trial.linked_asset ? String(trial.linked_asset) : '';
          return acc;
        }, {}),
      );
      setIndicationSelections(
        nextTrials.reduce<Record<number, string[]>>((acc, trial) => {
          acc[trial.id] = (trial.indication_codes || []).map(String);
          return acc;
        }, {}),
      );
      setPublishedFlags(
        nextTrials.reduce<Record<number, boolean>>((acc, trial) => {
          acc[trial.id] = Boolean(trial.trial_visible_approved);
          return acc;
        }, {}),
      );

      if (assetsResponse?.success) {
        const options = (assetsResponse.data || []).map((asset: { id: number; asset_name: string; company_name?: string }) => {
          const labelParts = [asset.asset_name];
          if (asset.company_name) labelParts.push(asset.company_name);
          return { id: asset.id, label: labelParts.filter(Boolean).join(' • ') };
        });
        setAssetOptions(options);
      }
      setIndicationOptions(
        (indications || []).map((ind: { id: string; name: string }) => ({ id: String(ind.id), name: ind.name })),
      );
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to load ongoing trials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadTrials(debouncedSearch);
  }, [debouncedSearch]);

  const indicationNameById = useMemo(() => {
    const m = new Map<string, string>();
    indicationOptions.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [indicationOptions]);

  const filteredTrials = useMemo(() => {
    if (debouncedSearch) return trials;
    if (!searchQuery.trim()) return trials;
    const query = searchQuery.toLowerCase();
    return trials.filter((trial) => {
      const indLabels = (trial.indication_codes || [])
        .map((c) => indicationNameById.get(String(c)) || '')
        .join(' ');
      const values = [
        trial.trial_name,
        trial.full_title,
        trial.nct_id,
        trial.indication_details,
        trial.trial_status,
        trial.overall_status,
        trial.sponsor,
        trial.asset_name,
        indLabels,
      ];
      return values.some((value) => value && String(value).toLowerCase().includes(query));
    });
  }, [trials, searchQuery, debouncedSearch, indicationNameById]);

  const handleRename = async (trial: OngoingTrialRow) => {
    const nextName = (editNames[trial.id] ?? trial.trial_name ?? '').trim();
    const nextTitle = (editTitles[trial.id] ?? trial.full_title ?? '').trim();
    const nextIndication = (editIndications[trial.id] ?? trial.indication_details ?? '').trim();
    const nextSponsor = (editSponsors[trial.id] ?? trial.sponsor ?? '').trim();
    if (!nextName) return;
    try {
      setSavingId(trial.id);
      const response = await apiService.renameOngoingTrial({
        trial_id: trial.id,
        trial_name: nextName,
        full_title: nextTitle || undefined,
        indication_details: nextIndication || undefined,
        sponsor: nextSponsor || undefined,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to rename trial');
      }
      await loadTrials(debouncedSearch);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to rename trial');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (trialId: number) => {
    if (!window.confirm('Delete this trial? This will unlink related data.')) return;
    try {
      setSavingId(trialId);
      const response = await apiService.deleteOngoingTrial({ trial_id: trialId });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to delete trial');
      }
      await loadTrials(debouncedSearch);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to delete trial');
    } finally {
      setSavingId(null);
    }
  };

  const handleAssetLink = async (trial: OngoingTrialRow) => {
    const rawValue = assetSelections[trial.id] ?? '';
    const assetId = rawValue ? Number(rawValue) : null;
    try {
      setSavingId(trial.id);
      const response = await apiService.updateOngoingTrialAsset({
        trial_id: trial.id,
        asset_id: assetId,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to update linked asset');
      }
      await loadTrials(debouncedSearch);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to update linked asset');
    } finally {
      setSavingId(null);
    }
  };

  const handleIndicationsSave = async (trial: OngoingTrialRow) => {
    const codes = (indicationSelections[trial.id] || []).map(Number).filter((n) => !Number.isNaN(n));
    try {
      setSavingId(trial.id);
      const response = await apiService.setOngoingTrialIndications({
        trial_id: trial.id,
        indication_codes: codes,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to update indications');
      }
      await loadTrials(debouncedSearch);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to update indications');
    } finally {
      setSavingId(null);
    }
  };

  const handlePublishedToggle = async (trial: OngoingTrialRow) => {
    const next = !(publishedFlags[trial.id] ?? Boolean(trial.trial_visible_approved));
    try {
      setSavingId(trial.id);
      setPublishedFlags((prev) => ({ ...prev, [trial.id]: next }));
      const response = await apiService.setOngoingTrialVisibility({
        trial_id: trial.id,
        mark: next,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to update visibility');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setPublishedFlags((prev) => ({ ...prev, [trial.id]: !next }));
      setError(e?.response?.data?.error || e?.message || 'Failed to update visibility');
    } finally {
      setSavingId(null);
    }
  };

  const toggleIndication = (trialId: number, indicationId: string) => {
    setIndicationSelections((prev) => {
      const current = new Set(prev[trialId] || []);
      if (current.has(indicationId)) current.delete(indicationId);
      else current.add(indicationId);
      return { ...prev, [trialId]: [...current] };
    });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-8 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Edit Emerging Trials</h1>
          <p className="text-sm text-slate-500">
            Link trials to assets and disease indications. Status per indication is managed on the asset page.
            {!debouncedSearch && ' Showing the 400 most recent trials — search to find others.'}
          </p>
        </div>
        <div className="w-full md:w-72">
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search trials..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Trial</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">NCT / Phase</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Asset</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Indications</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Published</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTrials.map((trial) => (
                <tr key={trial.id} className="hover:bg-slate-50 align-top">
                  <td className="px-3 py-2 w-[240px] max-w-[280px]">
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-slate-900 break-words">
                        {trial.trial_name || '—'}
                      </div>
                      <input
                        value={editNames[trial.id] ?? trial.trial_name ?? ''}
                        onChange={(event) =>
                          setEditNames((prev) => ({ ...prev, [trial.id]: event.target.value }))
                        }
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                        placeholder="Trial name"
                      />
                      <input
                        value={editTitles[trial.id] ?? trial.full_title ?? ''}
                        onChange={(event) =>
                          setEditTitles((prev) => ({ ...prev, [trial.id]: event.target.value }))
                        }
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                        placeholder="Full title"
                      />
                      <input
                        value={editSponsors[trial.id] ?? trial.sponsor ?? ''}
                        onChange={(event) =>
                          setEditSponsors((prev) => ({ ...prev, [trial.id]: event.target.value }))
                        }
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                        placeholder="Sponsor"
                      />
                      <button
                        type="button"
                        onClick={() => handleRename(trial)}
                        disabled={savingId === trial.id}
                        className="px-2 py-1 rounded-md text-[11px] font-semibold bg-slate-700 text-white disabled:opacity-60"
                      >
                        Save text
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(trial.id)}
                        disabled={savingId === trial.id}
                        className="px-2 py-1 rounded-md text-[11px] font-semibold border border-rose-200 text-rose-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                    <div className="font-mono text-xs">{trial.nct_id || '—'}</div>
                    <div className="text-xs mt-1">{trial.phases || '—'}</div>
                    <div className="text-[11px] text-slate-500 mt-1 max-w-[140px]">
                      {trial.trial_status || trial.overall_status || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2 min-w-[200px]">
                    <select
                      value={assetSelections[trial.id] ?? ''}
                      onChange={(event) =>
                        setAssetSelections((prev) => ({ ...prev, [trial.id]: event.target.value }))
                      }
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                    >
                      <option value="">Unlinked</option>
                      {assetOptions.map((asset) => (
                        <option key={asset.id} value={String(asset.id)}>
                          {asset.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => handleAssetLink(trial)}
                      disabled={savingId === trial.id}
                      className="mt-1 w-full px-2 py-1 rounded-md text-[11px] font-semibold border border-indigo-200 text-indigo-700 disabled:opacity-60"
                    >
                      Save asset
                    </button>
                  </td>
                  <td className="px-3 py-2 min-w-[220px]">
                    <div className="max-h-36 overflow-y-auto space-y-1 border border-slate-200 rounded-md p-2 bg-white">
                      {indicationOptions.map((ind) => {
                        const checked = (indicationSelections[trial.id] || []).includes(ind.id);
                        return (
                          <label key={ind.id} className="flex items-start gap-1.5 text-[11px] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleIndication(trial.id, ind.id)}
                              className="mt-0.5"
                            />
                            <span className={checked ? 'font-semibold text-slate-900' : 'text-slate-600'}>
                              {ind.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleIndicationsSave(trial)}
                      disabled={savingId === trial.id}
                      className="mt-1 w-full px-2 py-1 rounded-md text-[11px] font-semibold bg-indigo-600 text-white disabled:opacity-60"
                    >
                      Save indications
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={publishedFlags[trial.id] ?? Boolean(trial.trial_visible_approved)}
                        onChange={() => handlePublishedToggle(trial)}
                        disabled={savingId === trial.id}
                      />
                      Client visible
                    </label>
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap text-xs">
                    {formatDate(trial.last_updated_post_date || trial.last_update_submit_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageOngoingTrials;
